import { useEffect, useMemo, useRef, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Checkbox,
  Flex,
  HStack,
  Input,
  NativeSelectField,
  NativeSelectIndicator,
  NativeSelectRoot,
  PrimaryButton,
  RadioButton,
  TertiaryButton,
  Text,
  TextArea,
  VStack,
} from '@enechain/ecloud-designsystem'

import {
  IssuerSelector,
  useIssuerOptions,
} from '~/components/bidding/common/IssuerSelector'
import {
  areaOutsideBusinessAreasLabel,
  findIssuerOption,
  isAreaSelectableForIssuer,
  issuerAreaChoices,
} from '~/components/bidding/common/bidIssuer'
import {
  initialBidArea,
  initialBidAreas,
} from '~/components/bidding/common/lastBidArea'
import { FieldLabel, FieldRoot } from '~/components/common/Field'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { RequiredMark } from '~/components/common/RequiredMark'
import { TradeSideRadioGroup } from '~/components/common/TradeSideRadioGroup'
import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import { useCompanyInfo } from '~/hooks/useCompanyInfo'
import { convertAreaLabel } from '~/models/area'
import { KOMA_MW_TO_KWH } from '~/models/komaQuantity'
import { komaNumberToTime } from '~/models/komaToTime'
import { constrainNumberInput } from '~/models/numberInputConstraint'
import { BulkPreviewTable } from '~/pages/SpotBidding/components/BulkPreviewTable'
import {
  BulkVolumeModeField,
  FIXED_VOLUME_DEFAULT,
  bulkVolumeInputError,
} from '~/pages/SpotBidding/components/BulkVolumeModeField'
import { EmissionLinkingField } from '~/pages/SpotBidding/components/EmissionLinkingField'
import { FeeAutoConsiderHint } from '~/pages/SpotBidding/components/FeeAutoConsiderHint'
import { useSpotFeeAutoConsider } from '~/pages/SpotBidding/hooks/useSpotFeeAutoConsider'
import { useSpotFeeAutoConsiderPreviews } from '~/pages/SpotBidding/hooks/useSpotFeeAutoConsiderPreview'
import type {
  BulkAreaOption,
  BulkCreateParams,
  BulkFeeViolation,
  BulkGeneratedBid,
  BulkVolumeMode,
  ExistingBidVolume,
} from '~/pages/SpotBidding/models/bulkCreate'
import {
  buildBulkPreviewRows,
  buildBulkSpotBids,
  detectBulkLimitViolations,
  sumBulkVolumeMw,
} from '~/pages/SpotBidding/models/bulkCreate'
import {
  composeEmissionComment,
  isValidEmissionAlpha,
} from '~/pages/SpotBidding/models/emissionLinking'
import {
  isSpotFeePreviewBlocking,
  narrowestOutOfRangePreview,
  resolveSpotFeeSubmission,
  spotPriceEntryText,
} from '~/pages/SpotBidding/models/feePreview'
import {
  isSpotFeeAutoConsiderPending,
  priceInputConstraint,
} from '~/pages/SpotBidding/models/spotFeeAutoConsider'

type SideValue = 'buy' | 'sell'
type ModeValue = 'add' | 'replace'

type Props = {
  isOpen: boolean
  onClose: () => void
  /**
   * ページの受渡日。 発注主体区分の選択肢とエリアの選択可否をこの日の広域マスタで求める
   * (保存時の受渡契約コード解決と同じ断面。 #8842)。
   */
  deliveryDate: Date
  /** ページのエリアフィルタで単一エリアを指定している場合、 その AreaCode (AC-BSP-123)。 */
  defaultAreaCode?: AreaCode
  /**
   * 対象エリアの候補 (= checkbox に出す母集合、 JEPX 対象エリアを北→南順) と、
   * 数量の決め方 (mode) ごとの選択可否。 業務エリア設定 (AC-BSP-019 /
   * `~/hooks/useBusinessAreaFilter`) に含まれないエリアも候補に残し、 選べなくする
   * (AC-BSP-005b。 [[BulkAreaOption]] の `outsideBusinessAreas`)。
   *
   * 一括作成は複数エリアを対象にできる (AC-BSP-005a) ため、 page のエリア選択
   * (単一、 AC-BSP-016a) とは独立に持つ。 母集合と可否を 1 件にまとめているのは
   * [[BulkAreaOption]] の godoc の理由による (可否だけ別集合で渡すと、 母集合の
   * 外のエリアが silent に落ちる)。
   */
  areaOptions: readonly BulkAreaOption[]
  /**
   * エリア別・コマ別の need(MW) Map (= 合算しない、AC-005a 案B)。プレビュー / 生成の基礎データ。
   * 取得未完了 / エラー時は undefined (= needUnavailable と併用)。
   */
  needByArea?: ReadonlyMap<AreaCode, readonly number[]>
  /**
   * 追加モードで差し引く既存入札量 (= エリア × コマ × 方向の既存数量。 最終結果に
   * なっていない札の入札量と約定量、 AC-BSP-005a / `selectBulkAddExistingVolumes`)。
   */
  existingAddVolumes?: readonly ExistingBidVolume[]
  /**
   * `existingAddVolumes` を組めていない (= 通常入札かブロック入札の明細が未取得) とき true。
   * 「追加」 は差し引く量が欠けて既に出している量を二重に作るので、 Step2 へ進めない
   * (「置換」 は既存入札量を使わないので止めない)。
   */
  existingAddVolumesUnavailable?: boolean
  /**
   * 既存 DRAFT 札。 追加モードの階段数 (AC-BSP-021) / 上限数量(コマ計) の判定母集合に
   * 使う (差引には使わない。 差引は `existingAddVolumes`)。
   */
  existingDraftBids?: readonly ExistingBidVolume[]
  /**
   * 上限数量(コマ計) (AC-BSP-061 / AC-BSP-080) の合算母集合に加える、 既存の有効札
   * (入札中・承認申請中・承認済)。 `existingDraftBids` (下書き) とは別軸で、 モード
   * (追加 / 置換) に関わらず常に合算する。
   */
  existingActiveBids?: readonly ExistingBidVolume[]
  /** need RPC が取得エラー / 未取得のとき true (= 生成不可、モーダル内にエラー表示)。 */
  needUnavailable?: boolean
  /**
   * 確定時に **Step2 で表示した生成札そのもの** と生成パラメータを親へ渡す
   * (= 親が Create / Update RPC で永続化)。
   *
   * 親が params から札を作り直す形にすると「確認した行」と「書き込む行」が別々の
   * 生成呼び出しになり、`docs/principles.md` §1「注文・入札の内容と確認画面の表示が
   * 100% 一致」を「同じ props を両側へ配線している」という convention でしか満たせない。
   * 確定済みの配列を渡して一致を構造で保証する。
   */
  onSubmit: (
    params: BulkCreateParams,
    bids: readonly BulkGeneratedBid[],
  ) => Promise<void> | void
  isSubmitting?: boolean
  errorMessage?: string
  /**
   * 開いた時の発注主体区分 (= 画面上部ツールバーで選択中の区分。 AC-BSP-005d)。
   * 未指定・選択肢に無い時は [[IssuerSelector]] が優先順の最上位を既定選択する。
   */
  defaultIssuerType?: LicenseType
}

const SLOT_INDEXES = Array.from({ length: 48 }, (_, i) => i + 1)

/**
 * 一括作成モーダル (design `spot.html` `#bid-bulk-modal`、 AC-005)。
 *
 * 目標カバー率(%) / 売・買 / 入札価格 / コマ範囲 / 対象エリア(複数) / 既存入札の扱い
 * (追加 / 置換) を入力する Step1 と、 生成札の一覧 + 行単位の入札制限判定を確認する
 * Step2 の 2 ステップ確認フロー (AC-BSP-005、 design `#bblk-step2-body`)。
 * 確定時は親が既存の Create RPC をループ実行して永続化する (新規 bulk RPC は作らない)。
 */
export const BulkCreateModal = ({
  isOpen,
  onClose,
  deliveryDate,
  defaultAreaCode,
  areaOptions,
  needByArea,
  existingAddVolumes = [],
  existingAddVolumesUnavailable = false,
  existingDraftBids = [],
  existingActiveBids = [],
  needUnavailable = false,
  onSubmit,
  isSubmitting = false,
  errorMessage,
  defaultIssuerType = LicenseType.UNSPECIFIED,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // 開いた時点の値だけを使う (deps に入れると、 開いている間に値が変わった時に入力中の
  // 内容を初期値へ戻してしまう)。
  const defaultIssuerTypeRef = useRef(defaultIssuerType)
  defaultIssuerTypeRef.current = defaultIssuerType
  // Step1 (入力) → Step2 (内容確認) の 2 ステップ確認フロー (design `#bblk-step2-body`、
  // spec spot-bidding.md AC-BSP-005「プレビューは 2 ステップ確認フローとする」)。
  const [step, setStep] = useState<1 | 2>(1)
  // 数量の決め方 (AC-BSP-005a 目標カバー率 / AC-BSP-005c 数量指定)。
  const [volumeMode, setVolumeMode] = useState<BulkVolumeMode>('coverage')
  const [coverage, setCoverage] = useState('80')
  const [fixedVolume, setFixedVolume] = useState(FIXED_VOLUME_DEFAULT)
  const [side, setSide] = useState<SideValue>('buy')
  const [price, setPrice] = useState('10.00')
  const [slotFrom, setSlotFrom] = useState(1)
  const [slotTo, setSlotTo] = useState(48)
  const [areas, setAreas] = useState<Set<AreaCode>>(new Set())
  const [mode, setMode] = useState<ModeValue>('add')
  // 排出係数紐付け (spec §7) + メモ（自由記述）。 生成札全件へ同一適用する。
  const [emissionLinked, setEmissionLinked] = useState(false)
  const [emissionAlpha, setEmissionAlpha] = useState('')
  const [memoFree, setMemoFree] = useState('')
  // 発注主体区分。 生成札全件に同一適用する
  // (design `spot.html` `#bblk-issuer-selector`)。 未選択のうちは確定させない。
  const [issuerType, setIssuerType] = useState<LicenseType>(
    LicenseType.UNSPECIFIED,
  )
  const issuerOptions = useIssuerOptions(deliveryDate)
  const issuer = findIssuerOption(issuerOptions, issuerType)

  const isFixedMode = volumeMode === 'fixed'
  // checkbox に出す母集合。 可否は候補ごとに持つので母集合とずれない。
  const areaCodes = useMemo(
    () => areaOptions.map((option) => option.code),
    [areaOptions],
  )
  const outsideBusinessAreaCodes = useMemo(
    () =>
      new Set(
        areaOptions
          .filter((option) => option.outsideBusinessAreas)
          .map((option) => option.code),
      ),
    [areaOptions],
  )
  // 数量指定モードは need 非依存なので、 need の有無で絞らない可否を使う。
  const baseSelectableAreas = useMemo(
    () =>
      new Set(
        areaOptions
          .filter((option) =>
            isFixedMode
              ? option.selectableInFixedMode
              : option.selectableInCoverageMode,
          )
          .map((option) => option.code),
      ),
    [areaOptions, isFixedMode],
  )
  // 選択中の発注主体区分で選べないエリアは選べない (AC-BSP-005b)。
  // 区分が未選択のうちは絞り込まない。
  const activeSelectableAreas = useMemo(
    () =>
      new Set(
        [...baseSelectableAreas].filter((code) =>
          isAreaSelectableForIssuer(issuer, code),
        ),
      ),
    [baseSelectableAreas, issuer],
  )

  // 会社情報 (登録小売電気事業者 ID)。 未登録なら排出係数紐付け UI 非活性 (AC-BSP-087)。
  const companyInfo = useCompanyInfo()

  const initialAreaBasisRef = useRef({ defaultAreaCode, areaOptions })
  initialAreaBasisRef.current = { defaultAreaCode, areaOptions }

  // モーダルを開くたびに対象エリアを初期値 (AC-BSP-123) で初期化する。 他エリアは
  // ユーザーがチェックして追加する (= 複数エリア一括、 AC-BSP-005a)。
  // メモ / 排出係数紐付けも毎 open でクリアする (= 前回入力の残留を防ぐ)。
  // コマ範囲も全コマへ戻す (design `spot.html` は open 毎に slot-from=1 / slot-to=48 へリセットする。
  // 前回の開始コマが残ると終了コマ側の disabled も残る)。
  useEffect(() => {
    if (!isOpen) return
    setStep(1)
    setVolumeMode('coverage')
    setFixedVolume(FIXED_VOLUME_DEFAULT)
    const basis = initialAreaBasisRef.current
    setAreas(
      new Set(
        initialBidAreas('spot', basis.defaultAreaCode, (code) =>
          basis.areaOptions.some(
            (option) => option.code === code && option.selectableInCoverageMode,
          ),
        ),
      ),
    )
    setSlotFrom(1)
    setSlotTo(48)
    setEmissionLinked(false)
    setEmissionAlpha('')
    setMemoFree('')
    setIssuerType(defaultIssuerTypeRef.current)
  }, [isOpen])

  // チェック済みエリアが後から選択不可へ変わったら (= TradableAreas の refetch 等で
  // `areaOptions` の可否が落ちる)、state からも除去して checkbox 表示と確定対象を
  // 一致させる。
  useEffect(() => {
    setAreas((prev) => {
      if ([...prev].every((code) => activeSelectableAreas.has(code)))
        return prev
      return new Set(
        [...prev].filter((code) => activeSelectableAreas.has(code)),
      )
    })
  }, [activeSelectableAreas])

  const emissionActive = emissionLinked && companyInfo.registered

  const params = useMemo<BulkCreateParams>(() => {
    const common = {
      side: side === 'sell' ? Side.SELL : Side.BUY,
      priceYenPerKwh: price,
      slotFrom,
      slotTo,
      // state の prune (上の useEffect) は 1 render 遅れるため、確定対象は常に
      // 可否 (`areaOptions`) と同期交差する (= 選択不可エリアの札は生成・送信されない)。
      areaCodes: areaCodes.filter(
        (code) => areas.has(code) && activeSelectableAreas.has(code),
      ),
      mode,
      // 判別コメント完成形を memo_jepx として全生成札へ格納 (AC-BSP-086)。
      memoJepx:
        emissionActive && isValidEmissionAlpha(emissionAlpha)
          ? composeEmissionComment(companyInfo.retailerId, emissionAlpha)
          : undefined,
      memoFree: memoFree !== '' ? memoFree : undefined,
      // 発注主体区分は生成札全件へ同一適用する。 未選択のまま確定はできない
      // (下の isIssuerMissing で「作成」を止める)。
      issuerType: issuer?.issuerType ?? LicenseType.UNSPECIFIED,
    }
    // 数量は決め方ごとに別 field (= 使わない側のダミー値を積まない)。
    return volumeMode === 'fixed'
      ? { ...common, volumeMode: 'fixed', fixedVolumeMw: Number(fixedVolume) }
      : {
          ...common,
          volumeMode: 'coverage',
          coverageRatePercent: Number(coverage),
        }
  }, [
    volumeMode,
    coverage,
    fixedVolume,
    side,
    price,
    slotFrom,
    slotTo,
    areas,
    areaCodes,
    activeSelectableAreas,
    mode,
    emissionActive,
    emissionAlpha,
    companyInfo.retailerId,
    memoFree,
    issuer,
  ])

  /** 発注主体が未選択 (= 確定させない)。 */
  const isIssuerMissing = issuer === undefined

  // 入札制限 (SPOT_NORMAL) の実設定値。生成札の制限超過警告 (design (C)) に使う。
  const {
    limits: bidLimits,
    isLoading: isBidLimitsLoading,
    isError: isBidLimitsError,
  } = useBidLimits(BidSegment.SPOT_NORMAL)
  // 取得中 / 取得失敗の間は空 lookup が返る (= `useBidLimits` godoc) ため、
  // 「全エリア制限未設定 = 違反なし」と見分けが付かない。判定が成立していない状態を
  // OK と表示しないよう、判定関数へは制限を渡さない (undefined = 未判定)。
  //
  // 確定 (= DRAFT 作成) 自体は止めない: 入札制限で止めるのは spec 上 承認申請時
  // (AC-BSP-008) で、そのゲートは `SpotBidding/index.tsx` の isBidLimitsError →
  // limitUnavailable 節が既に fail-closed で持っている。 P0 の一括作成まで前倒しで
  // 止める spec 上の裏付けが無く、matrix RPC が恒常失敗する環境では下書き作成すら
  // できなくなるため、ここは「未判定」表示 + Alert で明示するに留める。
  const areBidLimitsUnavailable = isBidLimitsLoading || isBidLimitsError

  // ----- 手数料自動考慮 (AC-BDL-016 / AC-BSP-004b)。ON の値・値域・エリア別の
  // 入札制限は backend のプレビューだけを表示・保存する。 -----
  const { feeAutoConsider, isSettingsPending } = useSpotFeeAutoConsider(isOpen)
  // 価格欄の値 (数値)。 空欄は 0 ではなく未入力 (#11466 Q117-2「空欄は 0 と区別する」。 `Number('')` は 0)。
  const enteredPrice =
    price.trim() !== '' && Number.isFinite(Number(price))
      ? Number(price)
      : undefined
  const generatedBids = useMemo(
    () => buildBulkSpotBids(params, needByArea, existingAddVolumes),
    [params, needByArea, existingAddVolumes],
  )
  // ON の希望価格は 0・負数でも問い合わせ、 算出した入札価格の値域で判定する (#11466 Q117 = B)。
  // 個別作成と同じく、 数値の形をしていない入力だけは問い合わせない ([[spotPriceEntryText]])。
  const priceEntryText = spotPriceEntryText(price)
  const feePreviewInputs = useMemo(() => {
    if (priceEntryText === undefined) return []
    const generatedAreaCodes = Array.from(
      new Set(generatedBids.map((bid) => bid.areaCode)),
    )
    return generatedAreaCodes.map((areaCode) => ({
      areaCode,
      direction: params.side,
      desiredPriceYenPerKwh: priceEntryText,
    }))
  }, [generatedBids, params.side, priceEntryText])
  const {
    fee: previewFeeAutoConsider,
    previews: feePreviews,
    invalidInput: feePreviewInvalidInput,
  } = useSpotFeeAutoConsiderPreviews({
    isOpen,
    // 札を生成していない間も価格欄の案内を出すため、 問い合わせない 1 行 (idle) を置く
    inputs: feePreviewInputs.length > 0 ? feePreviewInputs : [undefined],
    fee: feeAutoConsider,
  })
  // 範囲外のエリアが複数ある時は、 希望価格の入力可能範囲が最も狭いものを案内する (AC-BSP-106 ※)。
  const narrowestOutOfRange = narrowestOutOfRangePreview(feePreviews)
  const feePreview = narrowestOutOfRange ?? feePreviews[0]
  // 範囲外のエリア名。 Step1 の価格欄の下で、 どのエリアが原因か分かるようにする (AC-BSP-106)。
  const outOfRangeAreaCodes = feePreviewInputs
    .filter((_, index) => feePreviews[index]?.kind === 'outOfRange')
    .map((input) => input.areaCode)
  const feeSubmissions = useMemo(
    () =>
      feePreviews.map((preview) =>
        resolveSpotFeeSubmission(t, {
          price,
          isMarket: false,
          fee: previewFeeAutoConsider,
          preview,
          rejectedByBackend: feePreviewInvalidInput,
        }),
      ),
    [t, price, previewFeeAutoConsider, feePreviews, feePreviewInvalidInput],
  )
  const narrowestIndex =
    narrowestOutOfRange === undefined
      ? -1
      : feePreviews.indexOf(narrowestOutOfRange)
  const feeSubmission =
    feeSubmissions[narrowestIndex] ??
    feeSubmissions.find((submission) => submission.kind === 'error') ??
    feeSubmissions.find((submission) => submission.kind === 'hold') ??
    feeSubmissions[0]

  // 決定 2026-09-16 (2-B): 一括作成は「入札制限が取れなくても下書きは作る」既存方針
  // (`areBidLimitsUnavailable` の godoc 参照) だが、 手数料考慮の設定 (ON/OFF) に限っては
  // 別: 読めるまで確定させない (codex-review 指摘 finding 5197f7e0876f、 未調整のまま
  // 発注するレース)。 手数料単価だけを取得できない間は OFF として扱い、 止めない
  // (AC-BSP-004b)。 他の入札制限項目 (階段数 / 上限数量等) の「確定は止めない」扱いは変えない。
  const isFeeAutoConsiderBlocking =
    isSettingsPending ||
    feePreviews.some((preview) => isSpotFeePreviewBlocking(preview, false))
  // 設定の取得中は価格欄を触れなくする (入札価格として入力した値が、 ON の確定で希望価格に
  // 読み替わって加減算されるのを防ぐ。 AC-BSP-004b)。 扱いを固定した後は読み替わらないので、
  // 再取得の失敗では触れなくしない。
  const isPriceEntryPending = isSpotFeeAutoConsiderPending(
    previewFeeAutoConsider,
  )
  // ON の価格欄は希望価格で、 算出した入札価格で判定する (#11466 Q117 = B)。 値域の両端を手数料単価ぶん広げる。
  const priceConstraint = priceInputConstraint(previewFeeAutoConsider)

  // プレビュー: 案B の生成札 (エリア × コマごとの実数量) を組み、件数・合計数量に加え、
  // settings「入札制限」超過を含むエリアを警告用に集約する。
  //
  // 合計数量は AC-BSP-053「一括作成プレビューの合計欄も MW（コマ別数量の単純合算値）とし、
  // MWh には換算しない（issue #4627 決定 B）」に従い MW 表記とする。 コマ別数量は保持・
  // 書込 contract まで MW で統一済み (issue #4285) なので `sumBulkVolumeMw` は単純合算で、
  // 単位換算は挟まない。 括弧内の kWh は AC-BSP-093 の補足表示 (= コマ 30 分あたりの MW を
  // kWh 化した値の単純合算 = × `KOMA_MW_TO_KWH`)。
  const preview = useMemo(() => {
    const feePreviewByArea = new Map(
      feePreviewInputs.map((input, index) => [
        input.areaCode,
        feePreviews[index],
      ]),
    )
    const feeSubmissionByArea = new Map(
      feePreviewInputs.map((input, index) => [
        input.areaCode,
        feeSubmissions[index],
      ]),
    )
    const feeViolationByArea = new Map<AreaCode, BulkFeeViolation>()
    // 手数料を加減算した札なのに入札価格が無い (preview が ready なのに値を持たない) 場合は、 希望価格を
    // 入札価格として見せず送らない。 確定を止める。
    let isFeeOrderPriceMissing = false
    const bids = generatedBids.map((bid): BulkGeneratedBid => {
      const feePreviewForArea = feePreviewByArea.get(bid.areaCode)
      const submission = feeSubmissionByArea.get(bid.areaCode)
      if (feePreviewForArea?.kind === 'outOfRange') {
        feeViolationByArea.set(bid.areaCode, {
          orderPriceYenPerKwh: feePreviewForArea.orderPriceYenPerKwh,
          violation: feePreviewForArea.violation,
        })
        return bid
      }
      if (submission?.kind !== 'send') return bid
      const confirmed = submission.feeAutoConsider
      if (confirmed === undefined) return bid
      if (!confirmed.enabled)
        return { ...bid, fee: { kind: 'notAdjusted', confirmed } }
      const orderPrice =
        feePreviewForArea?.kind === 'ready'
          ? feePreviewForArea.orderPriceYenPerKwh
          : undefined
      if (orderPrice === undefined) {
        isFeeOrderPriceMissing = true
        return bid
      }
      return {
        ...bid,
        priceYenPerKwh: orderPrice,
        fee: {
          kind: 'adjusted',
          desiredPriceYenPerKwh: params.priceYenPerKwh,
          confirmed,
        },
      }
    })
    // 未取得は undefined で渡す (= 空 lookup の「制限未設定」と区別する)。
    const limits = areBidLimitsUnavailable ? undefined : bidLimits
    // Step2 の行単位インライン判定 (AC-BSP-005:273「各行に対して入札制限 (AC-BSP-021 の
    // 階段数上限 / 上限数量) の判定結果を行単位でインライン表示」)。 判定結果は行に
    // 同梱されるので index 対応の parallel array を持たない。
    const rows = buildBulkPreviewRows(
      bids,
      limits,
      params.mode,
      existingDraftBids,
      existingActiveBids,
      feeViolationByArea,
    )
    const totalMw = sumBulkVolumeMw(bids)
    return {
      bids,
      rows,
      count: bids.length,
      totalMw,
      // AC-BSP-093 の括弧書き補足 (主表示は AC-BSP-053 の MW のまま)。
      totalKwh: totalMw * KOMA_MW_TO_KWH,
      // Step1 のエリア集約警告。 Step2 の行単位判定を集約するだけで判定基準を持たない
      // (`detectBulkLimitViolations` godoc)、 両ステップで判定基準が食い違わない。
      violatingAreas: detectBulkLimitViolations(rows),
      isFeeOrderPriceMissing,
    }
  }, [
    params,
    existingDraftBids,
    existingActiveBids,
    bidLimits,
    areBidLimitsUnavailable,
    generatedBids,
    feePreviewInputs,
    feePreviews,
    feeSubmissions,
  ])

  const rowUnknownCount = preview.rows.filter(
    (r) => r.status === 'unknown',
  ).length

  // 確定を止める違反 = 数量指定モード (AC-BSP-005c) の 上限数量(1札) 超過の行。
  //
  // 数量指定モードの札は `origin = BULK_FIXED_VOLUME` で送るので、backend の
  // `SpotBidUsecase.Create` が 上限数量(1札) ゲート (AC-BSP-103) で必ず拒否する
  // (自動起票扱いで免除されるのは目標カバー率モード = `BULK_TARGET_COVERAGE` だけ)。
  // 押せば必ずサーバーの汎用エラーになるボタンを押させない = 表示と実際の書込結果を
  // 一致させるための、 backend に対応する検証点がある UX 目的の事前チェック。
  // 出典: #8324 の EN 暫定判断 (2026-09-19、PdM の事後確認は #8404)
  // 「CSV 取込・一括作成 (数量指定モード) でも上限超過を行ごとに出し、確定を止める。
  //  一括作成は違反行があれば確定ボタンを押せなくする」。
  const blockingRowCount = isFixedMode
    ? preview.rows.filter((r) => r.violationKinds.includes('volume-max')).length
    : 0
  // 確定を止めない違反 (価格上下限・階段数、および目標カバー率モードの 上限数量) の件数。
  // こちらは従来どおり「作成はできるが承認申請で弾かれる」ので warning のまま扱う。
  const nonBlockingViolationCount =
    preview.rows.filter((r) => r.status === 'violation').length -
    blockingRowCount

  // 価格の入力の妥当性。 ON の希望価格は、 希望価格そのものの 0・負数で止めず、 backend が算出した入札価格の
  // 値域 (エリアごとのプレビューの範囲外 → `feeSubmission` の error) で判定する (#11466 Q117 = B / Q117-2)。
  // OFF は入力値が入札価格。
  const priceInputValid =
    enteredPrice !== undefined &&
    (previewFeeAutoConsider.kind === 'on' || enteredPrice > 0)
  // 排出係数紐付け ON なのに英字が不正 / 未入力なら確定不可 (AC-BSP-085 保存無効)。
  const emissionInvalid = emissionActive && !isValidEmissionAlpha(emissionAlpha)
  // 数量入力の不備 (決め方ごとの値域は [[BulkVolumeModeField]] 側が持つ)。
  const volumeInputError = bulkVolumeInputError(
    { volumeMode, coverage, fixedVolume },
    t,
  )
  const volumeInputValid = volumeInputError === undefined
  const isAddVolumesBlocking = mode === 'add' && existingAddVolumesUnavailable
  const canSubmit =
    // need 取得エラー時は古い need に基づく札を確定させない (= プレビューは以前の data で
    // count>0 に見えても Alert で「一括作成できません」を表示中のため確定を封じる)。
    // 数量指定モードは need を一切参照しないので (AC-BSP-005c)、 need 取得失敗でも
    // 確定できる (mock-app `GenerateBulkBidCandidates()` も fixed は gap 辞書不要)。
    (isFixedMode || !needUnavailable) &&
    !isAddVolumesBlocking &&
    !emissionInvalid &&
    // どの名義で発注するかが決まるまで確定させない (issue #5366)。
    !isIssuerMissing &&
    preview.count > 0 &&
    volumeInputValid &&
    priceInputValid &&
    // 手数料考慮の設定 (ON/OFF) が読めるまで確定できない (決定 2-B)。
    !isFeeAutoConsiderBlocking &&
    // 算出した入札価格が範囲外のエリアがある間は Step2 へ進めない (AC-BSP-106。 design の
    // `_bblkSave` と同じく Step1 で止める)。 Step2 の行の表示は、 万一ここを通った時の備え。
    narrowestOutOfRange === undefined &&
    !preview.isFeeOrderPriceMissing
  // Step2 確定ボタンのゲートは canSubmit (= 入力の妥当性 / 生成札の有無) と、
  // 上で求めた「確定を止める違反」の 2 つ。
  //
  // 上限数量(1札) 以外の判定結果 (価格上下限・階段数・目標カバー率モードの数量) は
  // 従来どおり **表示だけに使い、確定を止めない**:
  // - spec AC-BSP-005:273 は「制限違反の行を確定前に**検出できるようにする**」= 検出要件で、
  //   ブロックは承認申請時 (AC-BSP-008 / AC-BSP-021) の要件。 backend も DRAFT 離脱時
  //   (`ensureSpotBidDraftExitAllowed`) にしか検証しない。
  // - これらの軸は backend に対応する検証点が無く、 止めると frontend 再計算の業務判定
  //   だけで書込 (DRAFT 作成) の可否を決めることになる。
  // design `_bblkRenderStep2` は違反行が 1 件でもあれば commitBtn を disabled にするが、
  // design HTML は visual / UX intent の SoT であって enforcement 位置の SoT ではない。
  // 止めるのは backend が実際に拒否する軸だけに絞り、
  // 残りは Alert + 行バッジで明示する。
  const canCommit =
    canSubmit && blockingRowCount === 0 && feeSubmission?.kind === 'send'

  /**
   * 利用者が入力を不正な値へ変えた時だけ、footer に出す修正理由。
   *
   * 初期状態の発注主体未選択は必須表示、need 未取得と生成対象 0 件は本文先頭の Alert が
   * それぞれ担う。まだ何も操作していない段階から同じ理由を赤い文章で重ねると冗長なため、
   * footer では入力値の不備 (排出係数・数量の決め方に応じた目標カバー率 / 1 コマ
   * あたり数量・価格) だけを扱う (2026-09-04 デザイナー指摘)。
   */
  const inputErrorMessage = ((): string | undefined => {
    const key = (name: string): string => `page:biddingSpot.bulkCreate.${name}`
    if (emissionInvalid) return t(key('commitDisabledEmission'))
    // 数量の決め方で入力欄が入れ替わるので、 理由も出ている欄の方を指す
    // (AC-BSP-005c)。 数量指定モードでは目標カバー率欄が無く、 直しようのない
    // 欄を名指ししてしまうため。 出し分けは値域と同じ場所に置く。
    if (volumeInputError !== undefined) return volumeInputError
    if (!priceInputValid) {
      // 空欄は 0 と区別して未入力の文言 (AC-BSP-061。 ON は「希望価格を入力してください」) にする (#11466 Q117-2)。
      // ON は数値が入っていれば 0・負数でもここで止めないので、 「0 より大きい」 は OFF の 0 以下だけに出す。
      if (enteredPrice !== undefined) return t(key('commitDisabledPrice'))
      return t(
        previewFeeAutoConsider.kind === 'on'
          ? key('commitDisabledDesiredPriceRequired')
          : key('commitDisabledPriceRequired'),
      )
    }
    if (feeSubmission?.kind === 'error') return feeSubmission.message
    // 決定 2-B: 手数料考慮の設定が読めるまで確定できない理由も、 なぜ
    // ボタンが押せないのか分かるよう footer に出す (プレビュー欄と同じ文言)。
    if (previewFeeAutoConsider.kind === 'loading') {
      return t('page:biddingSpot.form.feeAutoConsiderLoadingHint')
    }
    // 扱いを固定した後に入札制限の再取得が失敗した時 (kind は on / off / fallbackOff のまま) も、
    // 確定を止めている live の isSettingsPending で理由を出す。
    if (previewFeeAutoConsider.kind === 'unavailable' || isSettingsPending) {
      return t('page:biddingSpot.form.feeAutoConsiderUnavailableHint')
    }
    return undefined
  })()

  // need を取得できていない / 取引不可のエリアは選択させない。 need Map に無いエリアは
  // buildBulkSpotBids が黙って skip する (= 札が silent に欠落) ため、選択自体を封じる。
  const isAreaSelectable = (code: AreaCode): boolean =>
    activeSelectableAreas.has(code)
  // 下の汎用の案内文 (need・取引可能エリアが理由) は、 発注主体・業務エリア以外の理由で
  // 選べないエリアがあるときだけ出す。 業務エリア外の理由は checkbox のラベルに出す。
  const hasUnselectableArea = areaOptions.some(
    (option) =>
      !option.outsideBusinessAreas && !baseSelectableAreas.has(option.code),
  )

  const toggleArea = (code: AreaCode, checked: boolean): void => {
    if (!isAreaSelectable(code)) return
    setAreas((prev) => {
      // 数量指定モードは単一エリア限定 (AC-BSP-005c)。 チェックした 1 件で置き換える
      // (checkbox の見た目のまま radio と同じ振る舞いにする。 design `spot.html` も
      // 同じ checkbox 群 + `#bblk-fixed-area-hint` の注記で表現している)。
      if (isFixedMode) return checked ? new Set([code]) : new Set()
      const next = new Set(prev)
      if (checked) next.add(code)
      else next.delete(code)
      return next
    })
  }

  /**
   * 数量の決め方を切り替える。 数量指定は単一エリア限定 (AC-BSP-005c) なので、
   * 切替時に選択を作り直す。 候補エリアが 1 つなら自動選択 (AC-BSP-005c)、 複数なら
   * AC-BSP-123 の初期値 (エリアフィルタの 1 エリア、 無ければ前回が 1 つの時のそのエリア)
   * を選び、 それも無ければ**選択を空にして**ユーザーの明示選択を待つ
   * (= 既定 10 MW × 48 コマが未確認のまま確定できる状態を作らない)。
   * design `spot.html` の `_bblkApplyInputMode` は「複数選択済みなら先頭 1 件を残す」
   * だが、 業務ルールの SoT は spec 側。
   */
  const handleVolumeModeChange = (next: BulkVolumeMode): void => {
    setVolumeMode(next)
    if (next !== 'fixed') return
    // 切替直後は isFixedMode がまだ更新前なので、 切替後のエリア集合を直接見る。
    const candidates = areaOptions
      .filter(
        (option) =>
          option.selectableInFixedMode &&
          isAreaSelectableForIssuer(issuer, option.code),
      )
      .map((option) => option.code)
    const initialArea = initialBidArea('spot', defaultAreaCode, (code) =>
      candidates.includes(code),
    )
    setAreas((prev) => {
      // 候補が 1 つだけなら自動選択 (選択済みでも同じ 1 件に収束する)。
      if (candidates.length === 1) return new Set(candidates)
      // 複数候補: 初期値のエリア (AC-BSP-123) が候補にあればそれ、 無ければ明示選択を求める。
      if (initialArea !== undefined) return new Set([initialArea])
      if (prev.size === 0) return prev
      return new Set()
    })
  }

  // 開始コマを終了コマより後ろへ動かしたら終了コマを追従させる (= 終了コマ側で
  // 開始コマより前を disabled にしているため、選択済み値が disabled のまま残らないように)。
  const handleSlotFromChange = (next: number): void => {
    setSlotFrom(next)
    setSlotTo((prev) => (prev < next ? next : prev))
  }

  // Step1 → Step2 (design `_bblkGoStep2`)。 ボタンは canSubmit で disabled になるが、
  // ハンドラ側でも明示的に遷移を拒否する (= 二重防御、他ボタンと同じ流儀)。
  const handleGoToStep2 = (): void => {
    if (!canSubmit) return
    setStep(2)
  }

  const handleSubmit = (): void => {
    // 二重防御はするが、 条件は canCommit と同一にする。 need 取得エラーの拒否は
    // canSubmit 側の `(isFixedMode || !needUnavailable)` が担っており、 ここで
    // `needUnavailable` を単独で見ると need を参照しない数量指定モード
    // (AC-BSP-005c) まで塞いでしまう (= ボタンは押せるのに何も起きない)。
    if (!canCommit) return
    // Step2 に描いた行そのものを渡す (= 表示と書込の 100% 一致、鉄則1)。
    void onSubmit(params, preview.bids)
  }

  return (
    <Modal
      footer={
        // ボタン間隔は design `.ecl-modal-footer` の `gap: 8px` に揃える
        // (2026-08-24 デザイナー指摘)。 Step1 は全モーダル共通の
        // [[SaveCancelFooter]] (`gap={2}` = 8px)、 Step2 は「フォームに戻る」が
        // 挟まる 3 ボタンなので同じ `gap={2}` の HStack で組む。
        // プレビュー (生成される札 / 合計数量) は **footer 側**に置く。
        // design `spot.html` は `#bblk-preview` をスクロール領域の外に出して
        // 常時可視にしており (「ecl-modal-footer と同じ固定表示」)、 本文に
        // 重ならない。 共通 [[Modal]] は body と footer の 2 口しか持たないので、
        // footer にプレビューと操作ボタンを縦に積んで同じ並びを作る
        // (2026-08-24 デザイナー指摘)。
        // Step2 ではプレビュー帯を出さない: 件数・合計は Step2 冒頭の説明文と
        // 行一覧そのものが担うため (design `_bblkStep2ShowView` も
        // `#bblk-preview` を隠す)。
        <VStack align="stretch" gap={0} width="100%">
          {step === 1 && (
            <>
              {/* 8080 の `#bblk-preview` は modal の**全幅**に敷かれた帯で、 左右に
                  余白が無く上境界線だけを持つ。 DS の footer は左右 24px / 上 16px の
                  padding を持つので、 負のマージンで打ち消して同じ全幅の帯にする
                  (2026-08-24 デザイナー指摘)。 角丸・左右下の枠線は付けない。 */}
              <Box
                marginTop={-4}
                borderColor="Border.infoMid"
                borderTopWidth="1px"
                backgroundColor="Surface.infoLow"
                marginInline={-6}
                paddingX={4}
                paddingY={3}
              >
                <Flex alignItems="baseline" justifyContent="space-between">
                  <Text color="Text.midEmphasis">
                    {t('page:biddingSpot.bulkCreate.previewCountLabel')}
                  </Text>
                  <Text>
                    <Text as="strong" fontSize="lg">
                      {preview.count}
                    </Text>{' '}
                    {t('page:biddingSpot.bulkCreate.previewCountSuffix')}
                  </Text>
                </Flex>
                {/* 主表示は AC-BSP-053 の MW (コマ別数量の単純合算)、 括弧内は
                    AC-BSP-093 の kWh 換算補足。 */}
                <Flex alignItems="baseline" justifyContent="space-between">
                  <Text color="Text.midEmphasis">
                    {t('page:biddingSpot.bulkCreate.previewVolumeLabel')}
                  </Text>
                  <Text>
                    <Text as="strong" fontSize="lg">
                      {preview.totalMw.toFixed(1)}
                    </Text>{' '}
                    {t('page:biddingSpot.bulkCreate.totalVolumeUnit', {
                      kwh: Math.round(preview.totalKwh).toLocaleString('ja-JP'),
                    })}
                  </Text>
                </Flex>
              </Box>
            </>
          )}
          <Flex
            alignItems="center"
            justifyContent="flex-end"
            gap={3}
            paddingTop={4}
          >
            {/* 入力値を誤った時の修正理由は**ボタンの横にそのまま出す**。
                初期状態の未選択や本文 Alert と同じ理由は重ねない。

                disabled のボタンは hover して初めて
                理由が出る形だと**理由があること自体に気付けない**ため、入力エラーがある間は
                常時見える位置に置く。 */}
            {inputErrorMessage !== undefined && (
              <Text color="Text.negativeMid" fontSize="sm" textAlign="right">
                {inputErrorMessage}
              </Text>
            )}
            {step === 1 ? (
              <SaveCancelFooter
                isSaveDisabled={!canSubmit}
                onCancel={onClose}
                onSave={handleGoToStep2}
                saveLabel={t('page:biddingSpot.bulkCreate.toStep2', {
                  count: preview.count,
                })}
              />
            ) : (
              <HStack gap={2}>
                <TertiaryButton onClick={onClose}>
                  {t('common:action.cancel')}
                </TertiaryButton>
                <TertiaryButton onClick={() => setStep(1)}>
                  {t('page:biddingSpot.bulkCreate.back')}
                </TertiaryButton>
                <PrimaryButton
                  disabled={!canCommit}
                  loading={isSubmitting}
                  onClick={handleSubmit}
                >
                  {t('page:biddingSpot.bulkCreate.commit', {
                    count: preview.count,
                  })}
                </PrimaryButton>
              </HStack>
            )}
          </Flex>
        </VStack>
      }
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size={step === 2 ? 'xl' : 'lg'}
      title={
        step === 2
          ? t('page:biddingSpot.bulkCreate.titleStep2')
          : t('page:biddingSpot.bulkCreate.title')
      }
    >
      <VStack align="stretch" gap={4}>
        {errorMessage !== undefined && (
          <Alert status="error" title={errorMessage} />
        )}
        {/* 需給ギャップ取得失敗は目標カバー率モードだけの問題 (数量指定モードは
            need を参照しない、 AC-BSP-005c)。 */}
        {needUnavailable && !isFixedMode && (
          <Alert
            description={t('page:biddingSpot.bulkCreate.needUnavailableBody')}
            status="error"
            title={t('page:biddingSpot.bulkCreate.needUnavailableTitle')}
          />
        )}
        {isAddVolumesBlocking && (
          <Alert status="error">
            {t('page:biddingSpot.serverError.detailUnresolved')}
          </Alert>
        )}
        {/* 入札制限を取得できなかった場合 (= 判定不能) は理由を明示する。 入札制限の
            判定自体 (階段数 / 上限数量等) は止める根拠が spec に無く承認申請ゲート側が
            fail-closed で持っているため warning のままだが、 同じ取得失敗で ON/OFF
            設定も未確定になる手数料考慮 (決定 2-B) は登録自体をブロックするため
            error にする。 取得中は一瞬で解決するので Alert を出さず、行バッジの
            「未判定」だけで表す (= 読込中に Alert が明滅するのを避ける)。 */}
        {isBidLimitsError && (
          <Alert
            description={t('page:biddingSpot.bulkCreate.limitUnavailableBody')}
            status="error"
            title={t('page:biddingSpot.bulkCreate.limitUnavailableTitle')}
          />
        )}
        {step === 1 ? (
          <>
            {/* 生成対象 0 件は「作成」 を押せない理由そのものなので、 プレビュー枠内の
                低コントラストな注記ではなく DS Alert で出す (2026-08-27 デザイナー指摘:
                なぜ作成ボタンが押せないのか分かりづらい)。 status は warning
                (= 人の対応を待つ / 注意が必要)。

                **本文の先頭**に置く (2026-09-04 デザイナー指摘)。 当初は footer の
                プレビュー帯とボタンの間に置いて「理由と操作を隣接させ」ていたが、
                この Alert は**入力しても消えない種類の行き止まり** (需給データが無い /
                対象エリア・コマ範囲が空) を告げるもので、 スクロール下端に置くと
                エリア・時間帯・カバー率・価格を一通り埋め終えてから気付くことになり、
                その入力がまるごと無駄になる。 先に見える位置へ出す。

                入力値を誤った時の理由 (カバー率・価格・排出係数) は footer のボタン脇に
                インラインで出す。 直す対象が下の入力欄なので、 操作の近くにある方が
                動線が短い。 Step2 ではプレビュー帯ごと出さないので Step1 限定。 */}
            {preview.count === 0 && (
              <Alert
                description={
                  // 数量指定モードは need を参照しないので、 需給データの取得状況を
                  // 案内しても直しようがない。 直せる対象 (単一エリア / コマ範囲 /
                  // 数量) だけを挙げた文言に差し替える (AC-BSP-005c)。
                  isFixedMode
                    ? t('page:biddingSpot.bulkCreate.noTargetFixed')
                    : t('page:biddingSpot.form.bulkCreateNoTargetDescription')
                }
                status="warning"
                title={t('page:biddingSpot.form.bulkCreateNoTargetTitle')}
              />
            )}
            {/* 入札制限超過の警告 (design (C)、AC-005)。生成札が settings「入札制限」の
                範囲外になる / 階段数上限を超えるエリアを列挙する (= UX 事前チェック、
                ブロックは承認申請時 / backend)。判定は Step2 の行単位バッジを集約した
                ものなので両ステップで結論が食い違わない。Step2 は行単位のインライン判定に
                切り替わるため Step1 でのみ表示する
                (design `_bblkStep2ShowView` の bblk-preview 同様)。 */}
            {preview.violatingAreas.length > 0 && (
              <Alert
                description={t(
                  'page:biddingSpot.bulkCreate.limitViolationBody',
                )}
                status="warning"
                title={t('page:biddingSpot.bulkCreate.limitViolationTitle', {
                  areas: preview.violatingAreas
                    .map((code) => convertAreaLabel(code))
                    .join('、'),
                })}
              />
            )}
            {/* 一括生成の説明。 design `spot.html` の `#bblk-mode-desc` は
                `font-size: var(--font-size-md)` (14px) + `--text-high-emphasis` で、
                実装だけ 12px / mid emphasis と弱かった (2026-08-24 デザイナー指摘)。 */}
            <Text color="Text.highEmphasis" fontSize="md">
              {isFixedMode
                ? t('page:biddingSpot.bulkCreate.descriptionFixed')
                : t('page:biddingSpot.bulkCreate.description')}
            </Text>

            {/* 発注主体区分。 生成される全札に同一適用する
                (design `spot.html` `#bblk-issuer-selector`)。 以前はこの欄が無く、
                一括作成の札は無言で発電になっていた (issue #5366)。 */}
            <IssuerSelector
              deliveryDate={deliveryDate}
              onChange={(next) => setIssuerType(next)}
              selectsDefault
              value={issuerType}
            />

            {/* 数量の決め方 (目標カバー率 / 数量指定) と、 決め方ごとに差し替わる
                数量入力欄。 プリセット値・値域は決め方に固有なので component 側に
                閉じ、 ここは値と切替ハンドラだけを渡す。 */}
            <BulkVolumeModeField
              coverage={coverage}
              fixedVolume={fixedVolume}
              onCoverageChange={setCoverage}
              onFixedVolumeChange={setFixedVolume}
              onVolumeModeChange={handleVolumeModeChange}
              volumeMode={volumeMode}
            />

            {/* 売/買 は select ではなく **radio** (design `spot.html` の `#bblk-dir` は
                `role="radiogroup"` + `.ecl-trade-label--buy/--sell` の色付きラベル)。
                2 択で色が意味を持つ項目なので、 開いて選ぶ select より両方が同時に
                見える radio が合う (2026-08-24 デザイナー指摘)。 個別作成 modal と
                同じ [[TradeSideRadioGroup]] を使う。
                [design 差異] 8080 は pill の右に「不足コマに入札」/「余剰コマに入札」の
                補足を置くが、 同じ内容が modal 冒頭の説明文にあり、 pill 内に入れると
                読み上げ名が「買 不足コマに入札」 になって個別作成の radio と揃わなく
                なるため落とす。 */}
            <FieldRoot>
              <FieldLabel>
                {t('page:biddingSpot.form.side')}
                <RequiredMark />
              </FieldLabel>
              <TradeSideRadioGroup
                ariaLabel={t('page:biddingSpot.form.side')}
                name="bulkCreate-side"
                onChange={setSide}
                value={side}
                values={{ buy: 'buy', sell: 'sell' }}
              />
            </FieldRoot>

            {/* 入札価格 (手数料自動考慮が ON の間は希望価格) */}
            <FieldRoot>
              <FieldLabel htmlFor="bulk-price">
                {t(
                  previewFeeAutoConsider.kind === 'on'
                    ? 'page:biddingSpot.form.desiredPriceYenPerKwh'
                    : 'page:biddingSpot.form.bidPrice',
                )}
                <RequiredMark />
              </FieldLabel>
              <Input
                width="160px"
                disabled={isPriceEntryPending}
                id="bulk-price"
                max={priceConstraint.max}
                min={priceConstraint.min}
                onChange={(e) =>
                  setPrice(
                    constrainNumberInput(e.target.value, priceConstraint),
                  )
                }
                step={priceConstraint.step}
                type="number"
                value={price}
              />
              {/* 手数料自動考慮 (AC-BDL-016 / AC-BSP-004b) の入札価格プレビュー。
                  全札に同一適用するため「全札の」文言 (variant="bulk") を使う。 調整後
                  の入札価格が JEPX 制度上限を外れる間はエラー文言に置き換える
                  (AC-BSP-050)。 */}
              <FeeAutoConsiderHint
                fee={previewFeeAutoConsider}
                isSettingsUnavailable={isSettingsPending}
                preview={feePreview}
                variant="bulk"
              />
              {outOfRangeAreaCodes.length > 0 && (
                <Text color="Text.negativeHigh" fontSize="sm">
                  {t('page:biddingSpot.bulkCreate.feeOutOfRangeAreas', {
                    areas: outOfRangeAreaCodes
                      .map((code) => convertAreaLabel(code))
                      .join(
                        t(
                          'page:biddingCommon.submitConfirm.items.areaSeparator',
                        ),
                      ),
                  })}
                </Text>
              )}
            </FieldRoot>

            {/* コマ範囲 (開始 / 終了の 2 control。 対象エリアと同じ理由で label だけを
                FieldRoot に包み、control id の重複付与を避ける) */}
            <VStack align="stretch" gap={1}>
              <FieldRoot>
                <FieldLabel>
                  {t('page:biddingSpot.blockExec.column.komaRange')}
                  <RequiredMark />
                </FieldLabel>
              </FieldRoot>
              <HStack gap={2}>
                <NativeSelectRoot width="150px">
                  <NativeSelectField
                    aria-label={t(
                      'page:biddingSpot.bulkCreate.slotFromAriaLabel',
                    )}
                    onChange={(e) =>
                      handleSlotFromChange(Number(e.target.value))
                    }
                    value={slotFrom}
                  >
                    {SLOT_INDEXES.map((k) => (
                      <option key={k} value={k}>
                        #{k} {komaNumberToTime(k)}
                      </option>
                    ))}
                  </NativeSelectField>
                  <NativeSelectIndicator />
                </NativeSelectRoot>
                <Text>〜</Text>
                <NativeSelectRoot width="150px">
                  <NativeSelectField
                    aria-label={t(
                      'page:biddingSpot.bulkCreate.slotToAriaLabel',
                    )}
                    onChange={(e) => setSlotTo(Number(e.target.value))}
                    value={slotTo}
                  >
                    {SLOT_INDEXES.map((k) => (
                      <option key={k} disabled={k < slotFrom} value={k}>
                        #{k} {komaNumberToTime(k)}
                      </option>
                    ))}
                  </NativeSelectField>
                  <NativeSelectIndicator />
                </NativeSelectRoot>
              </HStack>
            </VStack>

            {/* 対象エリア (複数選択。page で need 取得済みのエリアのみ選択可)
                Checkbox 群を FieldRoot の子に置くと Field context の control id が全
                checkbox の input と label(htmlFor) に重複付与され、ブラウザでは label
                クリックが常に先頭 (= 北海道) の input を叩いて他エリアが操作不能になる。
                label だけを FieldRoot に包み、checkbox 群は外に出す (既存
                AdminOcctoMaster/.../BusinessMasterFormFields の licenseTypes パターン)。 */}
            <VStack align="stretch" gap={1}>
              <FieldRoot>
                <FieldLabel>
                  {t('page:biddingSpot.bulkCreate.areaLabel')}
                  <RequiredMark />
                </FieldLabel>
              </FieldRoot>
              <Flex flexWrap="wrap" gap={3}>
                {/* 広域マスタ未登録のエリアは選択肢に出さない (AC-BSP-005b)。 */}
                {issuerAreaChoices(issuer, areaCodes, undefined).map(
                  ({ area }) => (
                    <Checkbox
                      key={area}
                      checked={areas.has(area)}
                      disabled={!isAreaSelectable(area)}
                      onCheckedChange={(details: {
                        checked: boolean | 'indeterminate'
                      }) => toggleArea(area, details.checked === true)}
                    >
                      {outsideBusinessAreaCodes.has(area)
                        ? areaOutsideBusinessAreasLabel(convertAreaLabel(area))
                        : convertAreaLabel(area)}
                    </Checkbox>
                  ),
                )}
              </Flex>
              {isFixedMode && (
                <Text color="Text.midEmphasis" fontSize="sm">
                  {t('page:biddingSpot.bulkCreate.fixedAreaSingleHint')}
                </Text>
              )}
              {/* 選択できない理由はモードごとに違う。 数量指定モードは need の
                  取得可否でエリアを絞らない (AC-BSP-005c) ので、 need を理由に
                  挙げると誤った案内になる。 */}
              {hasUnselectableArea && (
                <Text color="Text.lowEmphasis" fontSize="sm">
                  {isFixedMode
                    ? t('page:biddingSpot.bulkCreate.areaUnselectableHintFixed')
                    : t('page:biddingSpot.bulkCreate.areaUnselectableHint')}
                </Text>
              )}
            </VStack>

            {/* 既存入札の扱いも select ではなく **radio** (design `spot.html` の
                `#bblk-mode-label` は縦並びの `role="radiogroup"`)。 2 択で、 どちらを
                選ぶかで既存札が消えるかどうかが変わる項目なので、 開かずに両方の
                説明が読める radio にする (2026-08-24 デザイナー指摘)。
                ラベルが素の文字列なので DS `RadioButton` をそのまま使える
                (色 pill が要る売/買と違い、 自前 slot 組みが要らない)。 */}
            <FieldRoot>
              <FieldLabel>
                {t('page:biddingSpot.bulkCreate.modeLabel')}
                <RequiredMark />
              </FieldLabel>
              <RadioButton
                // DS `RadioButton` は内部で chakra `HStack` を使っており、 HStack が
                // `align="center"` を固定で入れるため `direction="column"` でも
                // 中央揃えのまま縦積みされ、 ラベル長が違うと丸の左位置がずれる
                // (2026-08-24 デザイナー指摘 / 同日実測)。 利用側の style props は
                // `RadioGroup.Root` にしか渡らず props では変えられないので、
                // nested selector で左揃えに上書きする
                // (`Supply48Data/components/FilterSidebar.tsx` と同じ回避)。
                // 8080 の `#bblk-mode-label` 直下の radiogroup も左揃え。
                direction="column"
                // 行間も詰める (2026-08-24 デザイナー指摘)。 DS の既定は
                // `HStack gap="4"` = 16px、 8080 は 6px。 DS spacing token に 6px の段は
                // 無いので、 最も近い段の spacing.2 (8px) を採る。
                css={{
                  '& .chakra-stack': { alignItems: 'flex-start', gap: '2' },
                }}
                name="bulkCreate-mode"
                onValueChange={(details) => {
                  if (details.value !== null)
                    setMode(details.value as ModeValue)
                }}
                radios={[
                  {
                    label: t('page:biddingSpot.bulkCreate.modeAdd'),
                    value: 'add',
                  },
                  {
                    label: t('page:biddingSpot.bulkCreate.modeReplace'),
                    value: 'replace',
                  },
                ]}
                value={mode}
              />
            </FieldRoot>

            {/* 排出係数紐付け (spec §7) + メモ（自由記述）。 design #bid-bulk-modal の
                自由記述欄グループ。 入力値は生成札全件へ同一適用する。 */}
            <EmissionLinkingField
              alpha={emissionAlpha}
              alphaError={
                emissionActive &&
                emissionAlpha !== '' &&
                !isValidEmissionAlpha(emissionAlpha)
                  ? t('page:biddingSpot.emission.alphaFormatError')
                  : undefined
              }
              fetchFailed={companyInfo.isError}
              idPrefix="bulkCreate"
              isLoading={companyInfo.isLoading}
              linked={emissionLinked}
              onAlphaChange={setEmissionAlpha}
              onLinkedChange={setEmissionLinked}
              registered={companyInfo.registered}
              retailerId={companyInfo.retailerId}
            />
            {/* `alignItems="stretch"`: DS `TextArea` は内部で縦 Stack に包むが、
                chakra の `Field.Root` は `align-items: flex-start` なので Stack が
                伸びずに内容幅 (実測 195px) で止まり、 中の textarea の `width: 100%`
                もそれに対して解決されて細くなる (2026-08-24 実測)。 */}
            <FieldRoot alignItems="stretch">
              <Flex
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <FieldLabel htmlFor="bulkCreate-memoFree">
                  {t('page:biddingSpot.form.memoFree')}
                </FieldLabel>
                <Text color="Text.lowEmphasis" fontSize="xs">
                  {`${memoFree.length} / 100`}
                </Text>
              </Flex>
              <TextArea
                id="bulkCreate-memoFree"
                maxLength={100}
                onChange={(e) => setMemoFree(e.target.value)}
                rows={2}
                value={memoFree}
              />
              <Text textStyle="fieldHint" color="Text.lowEmphasis">
                {t('page:biddingSpot.form.memoFreeHint')}
              </Text>
            </FieldRoot>
          </>
        ) : (
          <>
            {/* Step2 (内容確認): 生成される札を一覧で提示してから確定する
                (design `#bblk-step2-body`、 spec AC-BSP-005)。 */}
            <Text color="Text.midEmphasis" fontSize="sm">
              {t('page:biddingSpot.bulkCreate.stepConfirmHint', {
                count: preview.count,
              })}
            </Text>
            {/* 上限数量(1札) 超過で確定を止めている時は、押せない理由を error で出す
                (#8324 の EN 暫定判断)。 ここだけは「作成できない」= 失敗なので
                error を使い、下の warning
                (作成はできるが承認申請で弾かれる) と区別する。 */}
            {blockingRowCount > 0 && (
              <Alert
                description={t(
                  'page:biddingSpot.bulkCreate.rowVolumeLimitBlockedBody',
                )}
                status="error"
                title={t(
                  'page:biddingSpot.bulkCreate.rowVolumeLimitBlockedTitle',
                  {
                    count: blockingRowCount,
                  },
                )}
              />
            )}
            {/* Alert と行バッジは同じ事実 (違反 N 件) を指すので同じ semantic を使う。
                これらの違反は確定を止めず「人の対応を待つ」= warning。 */}
            {nonBlockingViolationCount > 0 && (
              <Alert
                description={t(
                  'page:biddingSpot.bulkCreate.rowLimitViolationExistsBody',
                )}
                status="warning"
                title={t(
                  'page:biddingSpot.bulkCreate.rowLimitViolationExistsTitle',
                  { count: nonBlockingViolationCount },
                )}
              />
            )}
            <BulkPreviewTable memoJepx={params.memoJepx} rows={preview.rows} />
            {/* 「OK」の意味範囲を表示側で限定する (design `spot.html:10067` が OK バッジの
                title に検証根拠を添えているのと同趣旨)。 総称 OK のままだと未判定軸
                (JEPX 制度上限 AC-BSP-050 / 既存ブロック札を含むコマ計) を隠してしまう。 */}
            <Text color="Text.lowEmphasis" fontSize="sm">
              {t('page:biddingSpot.bulkCreate.limitScopeNote')}
            </Text>
            {rowUnknownCount > 0 && (
              <Text color="Text.lowEmphasis" fontSize="sm">
                {t('page:biddingSpot.bulkCreate.limitUnknownNote', {
                  count: rowUnknownCount,
                })}
              </Text>
            )}
          </>
        )}
      </VStack>
    </Modal>
  )
}
